import React, { useState } from 'react';
import { Card, Typography, Table, Modal, Spin } from 'antd';
import { UserOutlined, PictureOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { getSignedUrl } from 'store/commonSlice';

const { Title, Text } = Typography;

const BORDER_STYLE = '1px solid #f0f0f0';

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: transparent;
		color: #8c8c8c;
		font-weight: 500;
		border-bottom: ${BORDER_STYLE};
		border-right: ${BORDER_STYLE};
	}

	.ant-table-tbody > tr > td {
		border-bottom: ${BORDER_STYLE};
		border-right: ${BORDER_STYLE};
	}

	.ant-table-thead > tr > th:last-child,
	.ant-table-tbody > tr > td:last-child {
		border-right: none;
	}
`;
const IconWrapper = styled.div`
	background-color: #fafafa;
	border: ${BORDER_STYLE};
	border-radius: 6px;
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 12px;
	cursor: pointer;
`;

function DriverCheckInCard({ driverCheckIn }) {
	const dispatch = useDispatch();
	const [modalVisible, setModalVisible] = useState(false);
	const [modalImage, setModalImage] = useState('');
	const [loadingImage, setLoadingImage] = useState(false);

	const openImageModal = async (fileUrl) => {
		if (!fileUrl) return;

		try {
			const fileKey = fileUrl.split('/').pop().split('?')[0];

			const data = {
				url_type: 'view',
				data: {
					entity: 'driver_selfie',
					keys: [fileKey],
				},
			};

			const res = await dispatch(getSignedUrl(data));

			const signedUrl = res?.data?.[0]?.url;

			if (!signedUrl) {
				return;
			}

			setModalVisible(true);
			setLoadingImage(true);
			setModalImage(signedUrl);
		} catch (error) {
			console.log(error);
		}
	};

	const columns = [
		{
			title: 'Check-In Time',
			key: 'time',
			render: (_, record) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<IconWrapper>
						<UserOutlined style={{ color: '#bfbfbf' }} />
					</IconWrapper>
					<Text strong>
						{record.checkin_time
							? moment(record.checkin_time)
									.add(5, 'hours')
									.add(30, 'minutes')
									.format('hh:mm A')
							: '-'}
					</Text>
				</div>
			),
		},
		{
			title: 'Captured Selfie',
			key: 'selfie',
			render: (_, record) => {
				let fileName = '-';

				if (record.selfie_url) {
					const urlWithoutQuery = record.selfie_url.split('?')[0];
					const fullName = urlWithoutQuery.split('/').pop();
					const parts = fullName.split('-');
					fileName = parts.length > 1 ? parts.slice(-1)[0] : fullName;
				}
				return (
					<button
						type="button"
						onClick={() => openImageModal(record.selfie_url)}
						style={{
							display: 'flex',
							alignItems: 'center',
							background: 'none',
							border: 'none',
							padding: 0,
							cursor: 'pointer',
						}}
					>
						<IconWrapper>
							<PictureOutlined style={{ color: '#bfbfbf' }} />
						</IconWrapper>

						<Text
							strong
							style={{
								textDecoration: 'underline',
								color: '#1890ff',
							}}
						>
							{fileName}
						</Text>
					</button>
				);
			},
		},
		{
			title: 'Check-In Status',
			dataIndex: 'checkin_status',
			key: 'checkin_status',
			render: (checkin_status) => {
				const isCompleted =
					checkin_status === 'VERIFIED' ||
					checkin_status === 'COMPLETED' ||
					checkin_status === 'Completed';
				const isPending = checkin_status === 'PENDING' || checkin_status === 'Pending';

				let displayStatus = checkin_status;
				let color = '#000';

				if (isCompleted) {
					displayStatus = 'Completed';
					color = '#52c41a';
				} else if (isPending) {
					displayStatus = 'Pending';
					color = '#faad14';
				}

				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<div
							style={{
								width: '6px',
								height: '6px',
								borderRadius: '50%',
								backgroundColor: color,
								marginRight: '8px',
							}}
						/>
						<Text strong style={{ color }}>
							{displayStatus}
						</Text>
					</div>
				);
			},
		},
	];

	return (
		<Card
			bodyStyle={{ padding: '0px' }}
			style={{ borderRadius: '8px', marginBottom: '16px', border: BORDER_STYLE }}
		>
			<div style={{ padding: '16px 24px', borderBottom: BORDER_STYLE }}>
				<Title level={5} style={{ margin: 0 }}>
					Driver Check-in Details
				</Title>
			</div>
			<div style={{ padding: '16px 24px' }}>
				<StyledTable
					columns={columns}
					dataSource={driverCheckIn}
					rowKey="id"
					pagination={false}
					bordered={false}
					size="middle"
				/>
			</div>
			<Modal
				open={modalVisible}
				footer={null}
				onCancel={() => setModalVisible(false)}
				centered
				width={500}
			>
				{loadingImage && (
					<div style={{ textAlign: 'center', padding: '40px' }}>
						<Spin size="large" />
					</div>
				)}
				<img
					key={modalImage}
					src={modalImage}
					alt="Selfie"
					style={{
						width: '100%',
						maxHeight: '500px',
						objectFit: 'contain',
					}}
					onLoad={() => setLoadingImage(false)}
					onError={() => {
						setLoadingImage(false);
					}}
				/>
			</Modal>
		</Card>
	);
}

DriverCheckInCard.propTypes = {
	driverCheckIn: PropTypes.array,
};

export default DriverCheckInCard;
